// hooks/useOnboardingTutorial.ts
// ─────────────────────────────────────────────────────────────────────────────
// Manages onboarding tutorial state with localStorage persistence.
// Provides a React context so any component can access tutorial state.
//
// DESIGN (v4 — 2026-09-08):
//   • The tutorial NO LONGER auto-starts and no longer owns the first-login
//     language/settings modal. That modal is now WelcomeSetupModal, dismissed
//     on its own terms; the tutorial is launched deliberately from the
//     dashboard ("Start Tutorial") or the user menu, and begins at
//     "Create a New Patient". This hook still tracks whether the welcome modal
//     has been seen, because both are first-run state.
//   • Only TWO steps require user action: "Create Patient" and "Send Message".
//   • Every other step advances via the "Next" button.
//   • Side-effects (expand form, prefill inputs, navigate) are performed by
//     the OnboardingOverlay — NOT by DOM-state checks.
//   • The overlay blocks ALL interaction with the underlying app except for
//     the two action steps above.
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Joan, 2026-09-11: the guided tutorial is BROKEN, so both entry points to it
 * are disabled rather than removed — the flow, its 16 steps and its anchors all
 * stay in the tree, and this is the single switch that brings them back.
 *
 * Disabled, not hidden: the control is still visible with a tooltip saying why,
 * so a user who was told "click Start Tutorial" finds it and learns it is
 * temporarily unavailable, instead of hunting for a button that vanished.
 *
 * Entry points gated by this: the empty-state dashboard's "Start Tutorial"
 * button (WelcomeHeroCard) and the user menu's "Quick Tutorial" item
 * (TopNavBarApp). Flip to true to restore both.
 */
export const TUTORIAL_ENABLED = false;


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TutorialStep {
  id: string;
  targetSelector: string; // CSS selector (usually [data-tour='...'])
  title: string;
  description: string;
  placement: "top" | "bottom" | "left" | "right";
  /**
   * When true the "Next" button is hidden. The tutorial only advances when
   * the user clicks the highlighted element (which fires the real element's
   * click and then auto-advances).
   */
  requiresAction?: boolean;
  /** Hint shown instead of the Next button for action steps. */
  actionHint?: string;
  /**
   * When true, the overlay renders a centered card with no spotlight.
   * The targetSelector is ignored for positioning and auto-skip.
   */
  noTarget?: boolean;
  /**
   * Highlight ring style:
   *   "default"       — periwinkle glow (standard)
   *   "orange"        — solid orange highlight ring
   *   "blink-orange"  — orange ring with blinking animation
   */
  highlightStyle?: "default" | "orange" | "blink-orange";
  /**
   * When true, the overlay renders the processing animation card.
   * The step auto-advances once the seed API returns AND a minimum
   * time has elapsed.
   */
  isProcessingStep?: boolean;
  /**
   * When true, the overlay lets clicks pass through to the app so the
   * user can freely explore (e.g. click sidebar items, scroll tables).
   * The tooltip card remains visible with Skip/Next buttons.
   */
  allowExplore?: boolean;
}

export interface TutorialContextValue {
  /** Whether the tutorial overlay is currently showing */
  isActive: boolean;
  /** Current step index (0-based) */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Current step definition, or null */
  step: TutorialStep | null;
  /** Advance to next step (or finish if last) */
  next: () => void;
  /** Go back one step */
  back: () => void;
  /** Skip/dismiss the tutorial */
  skip: () => void;
  /** Start the tutorial from the beginning */
  start: () => void;
  /** Whether the user has completed the tutorial at least once */
  isCompleted: boolean;
  /** Name of the patient created during the tutorial (if any) */
  tutorialPatientName: string | null;
  /** ID of the patient created during the tutorial (if any) */
  tutorialPatientId: number | null;
  /** Called by the patient creation flow to record the tutorial patient */
  setTutorialPatient: (name: string, id: number) => void;
  /** Whether the first-login workspace-setup modal should be shown */
  isWelcomeOpen: boolean;
  /** Dismiss the welcome modal permanently (does NOT start the tutorial) */
  dismissWelcome: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY_COMPLETED = "athena_tutorial_completed";
const STORAGE_KEY_STEP = "athena_tutorial_step";
const STORAGE_KEY_PATIENT_NAME = "athena_tutorial_patient_name";
const STORAGE_KEY_PATIENT_ID = "athena_tutorial_patient_id";
const STORAGE_KEY_SKIP_COUNT = "athena_tutorial_skip_count";
/** First-login workspace setup (language + AI + region). Independent of the
 *  tutorial: seeing one must not imply having seen the other. */
const STORAGE_KEY_WELCOME_DONE = "athena_welcome_completed";
/** After this many skips the tutorial is permanently dismissed. */
const MAX_SKIPS_BEFORE_PERMANENT_DISMISS = 3;

// ---------------------------------------------------------------------------
// Step Definitions (16 steps, 0-indexed)
//
// Language selection is NOT here. It lives in WelcomeSetupModal, so starting
// the tutorial drops the user straight into the thing they asked to be shown.
// ---------------------------------------------------------------------------

export const TUTORIAL_STEPS: TutorialStep[] = [
  // ── Step 0 ── (+) FAB button (dashboard)
  {
    id: "create-patient",
    targetSelector: "[data-tour='create-patient']",
    title: "tutorial.create_patient.title",
    description: "tutorial.create_patient.description",
    placement: "left",
  },

  // ── Step 1 ── Patient Form (name input prefilled)
  {
    id: "patient-form",
    targetSelector: "[data-tour='patient-creation-form']",
    title: "tutorial.patient_form.title",
    description: "tutorial.patient_form.description",
    placement: "bottom",
  },

  // ── Step 2 ── Create Patient button (user MUST click)
  {
    id: "submit-create-patient",
    targetSelector: "[data-tour='submit-create-patient']",
    title: "tutorial.submit_patient.title",
    description: "tutorial.submit_patient.description",
    placement: "bottom",
    requiresAction: true,
    actionHint: "tutorial.submit_patient.action_hint",
    highlightStyle: "blink-orange",
  },

  // ── Step 3 ── Sidebar overview (patient details page)
  {
    id: "sidebar-overview",
    targetSelector: "[data-tour='table-sidebar']",
    title: "tutorial.sidebar_overview.title",
    description: "tutorial.sidebar_overview.description",
    placement: "right",
  },

  // ── Step 4 ── Diagnosis & Patient Journey (sidebar domain section)
  {
    id: "sidebar-domain",
    targetSelector: "[data-tour='table-sidebar-domain']",
    title: "tutorial.sidebar_domain.title",
    description: "tutorial.sidebar_domain.description",
    placement: "right",
  },

  // ── Step 5 ── Clinical tables area (whole workspace: sidebar + data + chat)
  {
    id: "data-panel-overview",
    targetSelector: "[data-tour='patient-details-workspace']",
    title: "tutorial.data_panel.title",
    description: "tutorial.data_panel.description",
    placement: "bottom",
  },

  // ── Step 6 ── Floating chat bar (collapsed)
  {
    id: "floating-chat",
    targetSelector: "[data-tour='floating-chat-bar']",
    title: "tutorial.floating_chat.title",
    description: "tutorial.floating_chat.description",
    placement: "top",
  },

  // ── Step 7 ── Expand the chat (user MUST click)
  {
    id: "expand-chat",
    targetSelector: "[data-tour='floating-chat-expand']",
    title: "tutorial.expand_chat.title",
    description: "tutorial.expand_chat.description",
    placement: "top",
    requiresAction: true,
    actionHint: "tutorial.expand_chat.action_hint",
    highlightStyle: "blink-orange",
  },

  // ── Step 8 ── Chat Input (pre-filled with sample clinical note)
  {
    id: "chat-input",
    targetSelector: "[data-tour='floating-chat-panel']",
    title: "tutorial.chat_input.title",
    description: "tutorial.chat_input.description",
    placement: "top",
  },

  // ── Step 9 ── Send Message (user MUST click)
  {
    id: "send-message",
    targetSelector: "[data-tour='send-message']",
    title: "tutorial.send_message.title",
    description: "tutorial.send_message.description",
    placement: "top",
    requiresAction: true,
    actionHint: "tutorial.send_message.action_hint",
    highlightStyle: "blink-orange",
  },

  // ── Step 10 ── Extraction Processing (centered, animated)
  {
    id: "extraction-processing",
    targetSelector: "__none__",
    title: "tutorial.processing.title",
    description: "tutorial.processing.description",
    placement: "bottom",
    noTarget: true,
    isProcessingStep: true,
  },

  // ── Step 11 ── Data extracted (spotlight full workspace to show results)
  {
    id: "data-explained",
    targetSelector: "[data-tour='patient-details-workspace']",
    title: "tutorial.data_explained.title",
    description: "tutorial.data_explained.description",
    placement: "bottom",
  },

  // ── Step 12 ── Notifications Bell
  {
    id: "notifications",
    targetSelector: "[data-tour='notifications']",
    title: "tutorial.notifications.title",
    description: "tutorial.notifications.description",
    placement: "bottom",
  },

  // ── Step 13 ── Athena Logo / Home (auto-navigate on Next)
  {
    id: "athena-home",
    targetSelector: "[data-tour='athena-logo']",
    title: "tutorial.athena_home.title",
    description: "tutorial.athena_home.description",
    placement: "bottom",
  },

  // ── Step 14 ── Search & Results area (dashboard)
  {
    id: "search-patient",
    targetSelector: "[data-tour='patient-search-and-results']",
    title: "tutorial.search_patient.title",
    description: "tutorial.search_patient.description",
    placement: "right",
  },

  // ── Step 15 ── Congratulations (centered, no spotlight)
  {
    id: "tutorial-complete",
    targetSelector: "__none__",
    title: "tutorial.congrats.title",
    description: "tutorial.congrats.description",
    placement: "bottom",
    noTarget: true,
  },
];

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const TutorialContext = createContext<TutorialContextValue | null>(null);

export const useTutorial = (): TutorialContextValue => {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    // Return a safe no-op default when used outside the provider
    return {
      isActive: false,
      currentStep: 0,
      totalSteps: TUTORIAL_STEPS.length,
      step: null,
      next: () => {},
      back: () => {},
      skip: () => {},
      start: () => {},
      isCompleted: true,
      tutorialPatientName: null,
      tutorialPatientId: null,
      setTutorialPatient: () => {},
      isWelcomeOpen: false,
      dismissWelcome: () => {},
    };
  }
  return ctx;
};

// ---------------------------------------------------------------------------
// Hook (internal)
// ---------------------------------------------------------------------------

function useOnboardingTutorialState(): TutorialContextValue {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleted, setIsCompleted] = useState(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY_COMPLETED) === "true") return true;
      const skipCount = parseInt(
        localStorage.getItem(STORAGE_KEY_SKIP_COUNT) || "0",
        10
      );
      if (skipCount >= MAX_SKIPS_BEFORE_PERMANENT_DISMISS) return true;
      return false;
    } catch {
      return false;
    }
  });

  // Tutorial patient tracking
  const [tutorialPatientName, setTutorialPatientName] = useState<
    string | null
  >(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_PATIENT_NAME);
    } catch {
      return null;
    }
  });
  const [tutorialPatientId, setTutorialPatientId] = useState<number | null>(
    () => {
      try {
        const v = localStorage.getItem(STORAGE_KEY_PATIENT_ID);
        return v ? Number(v) : null;
      } catch {
        return null;
      }
    }
  );

  const setTutorialPatient = useCallback((name: string, id: number) => {
    setTutorialPatientName(name);
    setTutorialPatientId(id);
    try {
      localStorage.setItem(STORAGE_KEY_PATIENT_NAME, name);
      localStorage.setItem(STORAGE_KEY_PATIENT_ID, String(id));
    } catch {
      /* ignore */
    }
  }, []);

  // Guard against rapid auto-skip loops
  const skipCountRef = useRef(0);

  // First-login workspace setup. This is the ONLY thing that opens by itself.
  // The tutorial is never auto-started: a 16-step walkthrough that begins
  // without being asked for is an interruption, not onboarding.
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(false);
  const [isWelcomeDone, setIsWelcomeDone] = useState(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY_WELCOME_DONE) === "true") return true;
      // MIGRATION (v3 → v4). This modal used to be step 0 of the tutorial, so
      // anyone who completed or dismissed the tutorial has already seen it.
      // Without this, shipping v4 would show a "welcome" modal to every
      // EXISTING user on their next page load.
      if (localStorage.getItem(STORAGE_KEY_COMPLETED) === "true") return true;
      const skipCount = parseInt(
        localStorage.getItem(STORAGE_KEY_SKIP_COUNT) || "0",
        10
      );
      if (skipCount > 0) return true;
      return false;
    } catch {
      return true; // no storage: never nag
    }
  });

  useEffect(() => {
    if (isWelcomeDone) return;
    // Short delay so the modal lands on a rendered page, not a white flash.
    const timer = setTimeout(() => setIsWelcomeOpen(true), 1500);
    return () => clearTimeout(timer);
  }, [isWelcomeDone]);

  const dismissWelcome = useCallback(() => {
    setIsWelcomeOpen(false);
    setIsWelcomeDone(true);
    try {
      localStorage.setItem(STORAGE_KEY_WELCOME_DONE, "true");
    } catch {
      /* ignore */
    }
  }, []);

  // Reset skip counter when user manually changes step or starts fresh
  useEffect(() => {
    skipCountRef.current = 0;
  }, [currentStep]);

  const markCompleted = useCallback(() => {
    setIsActive(false);
    setIsCompleted(true);
    setCurrentStep(0);
    try {
      localStorage.setItem(STORAGE_KEY_COMPLETED, "true");
      localStorage.removeItem(STORAGE_KEY_STEP);
    } catch {
      /* localStorage unavailable */
    }
  }, []);

  const next = useCallback(() => {
    if (currentStep >= TUTORIAL_STEPS.length - 1) {
      markCompleted();
    } else {
      const nextIdx = currentStep + 1;
      setCurrentStep(nextIdx);
      try {
        localStorage.setItem(STORAGE_KEY_STEP, String(nextIdx));
      } catch {
        /* ignore */
      }
    }
  }, [currentStep, markCompleted]);

  const back = useCallback(() => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      try {
        localStorage.setItem(STORAGE_KEY_STEP, String(prevStep));
      } catch {
        /* ignore */
      }
    }
  }, [currentStep]);

  const skip = useCallback(() => {
    try {
      const count =
        parseInt(
          localStorage.getItem(STORAGE_KEY_SKIP_COUNT) || "0",
          10
        ) + 1;
      localStorage.setItem(STORAGE_KEY_SKIP_COUNT, String(count));
    } catch {
      /* ignore */
    }
    markCompleted();
  }, [markCompleted]);

  const start = useCallback(() => {
    skipCountRef.current = 0;
    setCurrentStep(0);
    setTutorialPatientName(null);
    setTutorialPatientId(null);
    setIsActive(true);
    setIsCompleted(false);
    try {
      localStorage.removeItem(STORAGE_KEY_COMPLETED);
      localStorage.setItem(STORAGE_KEY_STEP, "0");
      localStorage.removeItem(STORAGE_KEY_PATIENT_NAME);
      localStorage.removeItem(STORAGE_KEY_PATIENT_ID);
    } catch {
      /* ignore */
    }
  }, []);

  // -----------------------------------------------------------------------
  // Auto-skip steps whose target element does not exist in the DOM.
  // Steps with `noTarget` are never auto-skipped.
  //
  // Uses a polling approach: checks every 500 ms for up to 10 seconds.
  // This handles page navigation delays (e.g., after creating a patient,
  // the redirect to /patients/{id}/details takes time).
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!isActive) return;
    const stepDef = TUTORIAL_STEPS[currentStep];
    if (!stepDef) return;

    // Never auto-skip noTarget steps (language selector, congrats, etc.)
    if (stepDef.noTarget) return;

    const POLL_INTERVAL = 500;
    const MAX_WAIT = 10_000;
    let elapsed = 0;

    const interval = setInterval(() => {
      elapsed += POLL_INTERVAL;
      const el = document.querySelector(stepDef.targetSelector);
      if (el) {
        clearInterval(interval);
        skipCountRef.current = 0;
        return;
      }
      if (elapsed >= MAX_WAIT) {
        clearInterval(interval);
        if (skipCountRef.current < TUTORIAL_STEPS.length) {
          skipCountRef.current += 1;
          next();
        } else {
          markCompleted();
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [isActive, currentStep, next, markCompleted]);

  const step = isActive ? TUTORIAL_STEPS[currentStep] ?? null : null;

  return useMemo(
    () => ({
      isActive,
      currentStep,
      totalSteps: TUTORIAL_STEPS.length,
      step,
      next,
      back,
      skip,
      start,
      isCompleted,
      tutorialPatientName,
      tutorialPatientId,
      setTutorialPatient,
      isWelcomeOpen,
      dismissWelcome,
    }),
    [
      isActive,
      currentStep,
      step,
      next,
      back,
      skip,
      start,
      isCompleted,
      tutorialPatientName,
      tutorialPatientId,
      setTutorialPatient,
      isWelcomeOpen,
      dismissWelcome,
    ]
  );
}

// ---------------------------------------------------------------------------
// Provider (React component factory)
// ---------------------------------------------------------------------------

export { TutorialContext, useOnboardingTutorialState };

export default useTutorial;
